/**
 * Open eLMS SSO Integration Service
 * Handles token issuance and launching Open eLMS with pre-authentication
 */

/**
 * Issues an Open eLMS SSO token and launches Open eLMS
 * @param {string} userId - The user ID to create token for
 * @param {Object} options - Optional configuration
 * @param {number} options.ttlSeconds - Token time-to-live (60-1800 seconds)
 * @param {string} options.returnUrl - URL to return to after Open eLMS session
 * @returns {Promise<Object>} Result object with success status and details
 */
export async function launchOpenElmsSso(userId, options = {}) {
  try {
    const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
    if (!adminApiKey) {
      throw new Error('REACT_APP_ADMIN_API_KEY not configured');
    }

    const requestBody = {
      userId: userId,
      ttlSeconds: options.ttlSeconds || 300,
      returnUrl: options.returnUrl || window.location.origin,
    };

    let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!apiBaseUrl) {
      if (process.env.NODE_ENV === 'production') {
        const error = new Error('Configuration Error: REACT_APP_API_BASE_URL is not set in production');
        error.code = 'CONFIG_ERROR';
        throw error;
      }
      apiBaseUrl = 'http://localhost:5001';
    }
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    const fullUrl = `${apiBaseUrl}/providers/open-elms/issue-token`;

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': adminApiKey,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Failed to issue token');
    }

    const redirectUrl = data.redirectUrl || data.launchUrl;
    window.open(redirectUrl, "_blank", "noopener,noreferrer");

    return {
      success: true,
      token: data.token,
      launchUrl: data.launchUrl,
      expiresAt: data.expiresAt,
    };
  } catch (error) {
    const errorMessage = error.message || 'Failed to launch Open eLMS. Please try again.';
    return {
      success: false,
      error: errorMessage,
      details: error,
    };
  }
}

/**
 * Check if a provider is Open eLMS
 * @param {Object} provider - Provider object from database
 * @returns {boolean} True if this is Open eLMS provider
 */
export function isOpenElmsProvider(provider) {
  if (!provider) return false;

  // Check by provider key (most reliable)
  if (provider.key === 'open-elms' || provider.key === 'open_elms' || provider.key === 'openelms') {
    return true;
  }

  // Check by name (fallback)
  const name = (provider.name || '').toLowerCase();
  return (name.includes('open') && name.includes('elms')) || name.includes('openelms');
}

/**
 * Get user-friendly error messages for common Open eLMS SSO errors
 * @param {string} error - Error message
 * @returns {string} User-friendly error message
 */
export function getOpenElmsErrorMessage(error) {
  const errorLower = (error || '').toLowerCase();

  if (errorLower.includes('unauthorized') || errorLower.includes('api key')) {
    return 'Authentication failed. Please contact support.';
  }

  if (errorLower.includes('user_not_found')) {
    return 'User account not found. Please contact support.';
  }

  if (errorLower.includes('popup')) {
    return 'Please allow popups for this site and try again.';
  }

  if (errorLower.includes('network') || errorLower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  if (errorLower.includes('token') && errorLower.includes('expired')) {
    return 'Session expired. Please try again.';
  }

  return error || 'An unexpected error occurred. Please try again.';
}

